import React from "react";
import { FormattedDate, FormattedMessage, FormattedNumber } from "react-intl";

import Box from "@mui/material/Box";
import CheckIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Cancel";

import Footnote from "components/Footnote";
import { Typography } from "@mui/material";

interface ITupel {
  label?: string;
  labelId?: string;
  text?: string;
  textId?: string;
  date?: string;
  unit?: string;
  number?: number;
  float?: number;
  percent?: number;
  boolean?: boolean;
  euro?: number;
  boxProps?: any;
  footnote?: number;
  footnotePosition?: "left" | "right";
  force?: boolean;
  divider?: boolean;
  digits?: number;
  property?: string;
  information?: string;
  informationPosition?: string;
}

const Tupel: React.FC<ITupel> = ({
  label,
  labelId,
  text,
  textId,
  number,
  float,
  unit,
  percent,
  date,
  boolean,
  euro,
  boxProps = {},
  footnote,
  footnotePosition = "left",
  force = false,
  divider = true,
  digits = 0,
  property,
  information,
  informationPosition = "left",
}): JSX.Element | null => {
  if (force && float === null) return <p>{label} fehlt</p>;
  if (force && number === null) return <p>{label} fehlt</p>;
  if (force && percent === null) return <p>{label} fehlt</p>;
  if (force && euro === null) return <p>{label} fehlt</p>;

  var LabelElement = <span>&nbsp;</span>;

  if (label && labelId) {
    LabelElement = (
      <span>
        <FormattedMessage id={labelId} defaultMessage={label} />
      </span>
    );
  } else if (label) {
    LabelElement = <span>{label}</span>;
  } else if (labelId) {
    LabelElement = (
      <span>
        <FormattedMessage id={labelId} defaultMessage={`label-${labelId}`} />
      </span>
    );
  }

  let ValueElement;
  if (textId !== undefined) {
    ValueElement = (
      <FormattedMessage
        id={textId}
        defaultMessage={text || `label-${textId}`}
      />
    );
  } else if (text !== undefined) {
    if ((!text || text.trim().length === 0) && !force === true) return null;
    ValueElement = <span>{text}</span>;
  } else if (validate(number)) {
    ValueElement = (
      <FormattedNumber
        value={number || 0}
        minimumFractionDigits={digits}
        maximumFractionDigits={digits}
      />
    );
  } else if (validate(float)) {
    ValueElement = (
      <FormattedNumber value={float || 0} minimumFractionDigits={digits} />
    );
  } else if (percent !== undefined) {
    if (!percent && !force === true) return null;
    ValueElement = (
      <FormattedNumber
        value={percent}
        // eslint-disable-next-line react/style-prop-object
        style="percent"
        minimumFractionDigits={digits}
        maximumFractionDigits={digits}
      />
    );
  } else if (euro !== undefined) {
    if (!euro && !force === true) return null;
    ValueElement = (
      // eslint-disable-next-line react/style-prop-object
      <FormattedNumber
        value={euro}
        style="currency"
        currency="EUR"
        minimumFractionDigits={digits}
        maximumFractionDigits={digits}
      />
    );
  } else if (date !== undefined) {
    if ((!date || date.trim().length === 0) && !force === true) return null;
    const dateString = date.length <= 8 ? "01/".replace("/", ".") + date : date;
    const dateObject = new Date(dateString);
    if (!dateObject) return null;
    ValueElement = (
      <FormattedDate
        value={dateObject}
        year="numeric"
        month="long"
        day="2-digit"
      />
    );
  } else if (boolean !== undefined) {
    ValueElement = boolean ? (
      <CheckIcon fontSize="inherit" sx={{ color: "success.main" }} />
    ) : (
      <CloseIcon fontSize="inherit" sx={{ color: "error.main" }} />
    );
  }
  if (!ValueElement) return null;

  return (
    <>
      <Box
        data-testid="tupel"
        sx={{
          display: "flex",
          py: 1,
          borderBottom: divider ? 1 : "none",
          justifyContent: "space-between",
          ...boxProps,
        }}
      >
        <span>
          {LabelElement}
          {footnote && footnotePosition === "left" && (
            <Footnote id={footnote} />
          )}
          {information && informationPosition === "left" && (
            <Typography variant="body2">{information}</Typography>
          )}
        </span>
        <Box component="span" sx={{ textAlign: "right" }}>
          {ValueElement}
          {unit && ` ${unit}`}
          {footnote && footnotePosition === "right" && (
            <Footnote id={footnote} />
          )}
          {information && informationPosition === "right" && (
            <Typography variant="body2">{information}</Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Tupel;

const validate = (e: any): boolean => !(e === null || e === undefined);
